import React, { useState, useEffect } from "react";
import { ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import AOS from "aos";
import "aos/dist/aos.css";

const FieldRentalsSection = () => {
  const [rentals, setRentals] = useState([]);
  const navigate = useNavigate();

  // ✅ Initialize AOS
  useEffect(() => {
    AOS.init({
      duration: 800,
      easing: "ease-in-out",
      once: true,
      offset: 50,
    });
  }, []);

  const allRentals = () => {
    let data = "";

    let config = {
      method: "get",
      maxBodyLength: Infinity,
      url: `${process.env.REACT_APP_API_URL}/fields-rental/all-fields`,
      headers: {},
      data: data,
    };

    axios
      .request(config)
      .then((response) => {
        console.log(JSON.stringify(response.data));
        setRentals(response?.data?.rentals);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  useEffect(() => {
    allRentals();
  }, []);

  const handleMoreClick = () => {
    navigate("/all-rental-fields");
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleFieldSelect = (rental) => {
    navigate("/rental", {
      state: {
        selectedField: rental,
        shouldScrollToCalendar: true,
      },
    });
  };

  const displayedRentals = rentals.slice(0, 7);
  const hasMoreRentals = rentals.length > 7;

  const RentalCard = ({ rental, height = "h-64", index = 0 }) => (
    <div
      className={`group relative overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-1 bg-black ${height} mb-4 cursor-pointer`}
      onClick={() => handleFieldSelect(rental)}
      data-aos="fade-up"
      data-aos-delay={index * 100}
    >
      <div className="relative w-full h-full overflow-hidden">
        <img
          src={rental?.imageUrl}
          alt={rental?.fieldName}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 opacity-90"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
        <div className="absolute bottom-5 max-w-[70%] left-4 right-4 z-10">
          <h3 className="text-white font-semibold font-heading text-sm sm:text-base md:text-lg tracking-wide uppercase leading-tight">
            {rental?.fieldName}
          </h3>
        </div>
        <div className="absolute px-4 py-5 inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-all duration-300 flex flex-col items-center justify-between">
          <div className="flex items-center w-full justify-between">
            <span className="px-3 py-1 text-[#FF4141] bg-white text-xs sm:text-sm font-heading font-semibold rounded-md leading-relaxed shadow-lg tracking-wider">
              ${rental?.price} {rental?.time}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="px-4 md:px-8 lg:px-12 pb-12">
      <div
        className="flex items-center justify-between mb-10"
        data-aos="fade-up"
      >
        <h2 className="font-heading text-xl md:text-3xl font-bold tracking-wide">
          FIELD RENTALS
        </h2>
        {hasMoreRentals && (
          <button
            onClick={handleMoreClick}
            className="flex items-center gap-2 px-4 py-2 bg-[#FF4141] hover:bg-red-600 text-white font-semibold rounded-md transition-all duration-300 transform hover:scale-105 shadow-lg"
            data-aos="zoom-in"
          >
            <span className="text-sm md:text-base">More</span>
            <ArrowRight className="w-4 h-4 md:w-5 md:h-5" />
          </button>
        )}
      </div>

      {/* 💻 Desktop Grid */}
      <div
        className="hidden lg:grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4"
        data-aos="fade-up"
      >
        <div className="md:col-span-1">
          {displayedRentals[0] && (
            <RentalCard
              rental={displayedRentals[0]}
              height="h-96 md:h-[35rem]"
              index={0}
            />
          )}
        </div>

        <div className="md:col-span-1 space-y-4">
          {displayedRentals[1] && (
            <RentalCard
              rental={displayedRentals[1]}
              height="h-48 md:h-[17rem]"
              index={1}
            />
          )}
          {displayedRentals[2] && (
            <RentalCard
              rental={displayedRentals[2]}
              height="h-48 md:h-[17rem]"
              index={2}
            />
          )}
        </div>

        <div className="md:col-span-1">
          {displayedRentals[3] && (
            <RentalCard
              rental={displayedRentals[3]}
              height="h-96 md:h-[35rem]"
              index={3}
            />
          )}
        </div>

        <div className="md:col-span-1 space-y-4">
          {displayedRentals[4] && (
            <RentalCard
              rental={displayedRentals[4]}
              height="h-48 md:h-[17rem]"
              index={4}
            />
          )}
          {displayedRentals[5] && (
            <RentalCard
              rental={displayedRentals[5]}
              height="h-48 md:h-[17rem]"
              index={5}
            />
          )}
        </div>

        <div className="md:col-span-1">
          {displayedRentals[6] && (
            <RentalCard
              rental={displayedRentals[6]}
              height="h-96 md:h-[35rem]"
              index={6}
            />
          )}
        </div>
      </div>

      {/* 📱 Mobile & Tablet Grid */}
      <div
        className="grid lg:hidden grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4"
        data-aos="fade-up"
      >
        <div className="md:col-span-1">
          {displayedRentals[0] && (
            <RentalCard
              rental={displayedRentals[0]}
              height="h-96 md:h-[35rem]"
              index={0}
            />
          )}
        </div>

        <div className="md:col-span-1 space-y-4">
          {displayedRentals[1] && (
            <RentalCard
              rental={displayedRentals[1]}
              height="h-48 md:h-[17rem]"
              index={1}
            />
          )}
          {displayedRentals[2] && (
            <RentalCard
              rental={displayedRentals[2]}
              height="h-48 md:h-[17rem]"
              index={2}
            />
          )}
        </div>

        <div className="md:col-span-1">
          {displayedRentals[3] && (
            <RentalCard
              rental={displayedRentals[3]}
              height="h-96 md:h-[35rem]"
              index={3}
            />
          )}
        </div>

        <div className="md:col-span-2 space-y-4">
          {displayedRentals[4] && (
            <RentalCard
              rental={displayedRentals[4]}
              height="h-48 md:h-[17rem]"
              index={4}
            />
          )}
          {displayedRentals[5] && (
            <RentalCard
              rental={displayedRentals[5]}
              height="h-48 md:h-[17rem]"
              index={5}
            />
          )}
        </div>

        <div className="md:col-span-1">
          {displayedRentals[6] && (
            <RentalCard
              rental={displayedRentals[6]}
              height="h-96 md:h-[35rem]"
              index={6}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default FieldRentalsSection;